/* eslint-disable react-refresh/only-export-components */

import React, { useCallback, useContext, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { useU2000GridStateHandlers } from "../u2000Core/reducer/formReducer/U2000GridStateHandlers";
import { useU2000SearchStateHandlers } from "../u2000Core/reducer/formReducer/U2000SearchStateHandlers";
import { useU2000LUStateHandlers } from "../u2000Core/reducer/formReducer/U2000LUHandlers";
import type { U2000GridStateHandlersReturn } from "../u2000Core/reducer/formReducer/U2000GridStateHandlers";
import type { U2000SearchStateHandlersReturn } from "../u2000Core/reducer/formReducer/U2000SearchStateHandlers";
import type { U3130InitLU, U3130TaskItem, U3130Timesheet, U3130InitLUEmployee, U3130ApprovalListPreset } from "./U3130TimesheetModel";
import type { U2101InitLU } from "../u2101LogicalUnit/U2101";
import { U2000_ORG_DISPLAY_TIMEZONE, u2000SetActiveDisplayTimezone } from "../u2000Core/tools/U2000DateTimeHelpers";

export interface U3130ContextType {
  initLU: U3130InitLU;
  luStateHandlers: ReturnType<typeof useU2000LUStateHandlers>;
  searchStateHandlers: U2000SearchStateHandlersReturn;
  gridStateHandlers: U2000GridStateHandlersReturn<U3130Timesheet>;
  /** Employé initLU (optionnel) — repli soldes banques si absent du GET. */
  employee: U3130InitLUEmployee | null;
  honorariumTasksByClientRowId: Record<string, U3130TaskItem[]>;
  setHonorariumTasksForRow: (clientRowId: string, tasks: U3130TaskItem[]) => void;
  /** Préréglage filtre liste (statut / période) — partagé liste ↔ navigation détail. */
  approvalListPreset: U3130ApprovalListPreset;
  setApprovalListPreset: (preset: U3130ApprovalListPreset) => void;
  approvalListPresetInitialized: boolean;
  setApprovalListPresetInitialized: (value: boolean) => void;
  /** Onglet actif du détail feuille (Honoraires / Dépenses / Équipement) — mémoire session, pas localStorage. */
  formActiveTab: string;
  setFormActiveTab: (tab: string) => void;
}

interface U3130ContextProviderProps {
  initLU: U3130InitLU;
  children: ReactNode;
}

const U3130Context = React.createContext<U3130ContextType | undefined>(undefined);

export const U3130ContextProvider: React.FC<U3130ContextProviderProps> = ({ initLU, children }) => {
  u2000SetActiveDisplayTimezone(initLU.organizationTimezone ?? U2000_ORG_DISPLAY_TIMEZONE);

  const luStateHandlers = useU2000LUStateHandlers({
    initLU: initLU as unknown as U2101InitLU,
  });
  const searchStateHandlers = useU2000SearchStateHandlers({
    luName: "u3130",
    launchSearch: initLU.launchSearch,
    hideSearch: initLU.hideSearch,
  });
  const gridStateHandlers = useU2000GridStateHandlers<U3130Timesheet>({
    nbRecordPerPage: initLU.nbRecordPerPage ?? 50,
  });
  const [honorariumTasksByClientRowId, setHonorariumTasksByClientRowId] = useState<Record<string, U3130TaskItem[]>>({});
  const [approvalListPreset, setApprovalListPreset] = useState<U3130ApprovalListPreset>(
    () => initLU.defaultApprovalListPreset ?? "waiting_for_approval",
  );
  const [approvalListPresetInitialized, setApprovalListPresetInitialized] = useState(false);
  const [formActiveTab, setFormActiveTab] = useState<string>("tabHonoraria");
  const employee = initLU.employee ?? null;

  const setHonorariumTasksForRow = useCallback((clientRowId: string, tasks: U3130TaskItem[]) => {
    setHonorariumTasksByClientRowId((prev) => ({ ...prev, [clientRowId]: tasks }));
  }, []);

  const contextValue = useMemo<U3130ContextType>(
    () => ({
      initLU,
      luStateHandlers,
      searchStateHandlers,
      gridStateHandlers,
      employee,
      honorariumTasksByClientRowId,
      setHonorariumTasksForRow,
      approvalListPreset,
      setApprovalListPreset,
      approvalListPresetInitialized,
      setApprovalListPresetInitialized,
      formActiveTab,
      setFormActiveTab,
    }),
    [
      initLU,
      luStateHandlers,
      searchStateHandlers,
      gridStateHandlers,
      employee,
      honorariumTasksByClientRowId,
      setHonorariumTasksForRow,
      approvalListPreset,
      approvalListPresetInitialized,
      formActiveTab,
    ],
  );

  return <U3130Context.Provider value={contextValue}>{children}</U3130Context.Provider>;
};

export function useU3130Context(): U3130ContextType {
  const context = useContext(U3130Context);
  if (!context) {
    throw new Error("useU3130Context must be used within a U3130ContextProvider");
  }
  return context;
}
